import {
  Box, Card, CardHeader, Skeleton,
} from '@mui/material';
import {
  FC, useEffect, useMemo, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import Chart, { useChart, ApexOptions } from '@mgm/common/components/charts';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getDailyStateSelectedLocation, getIsOrdersLoading, getLocationStats } from '../../../../store/orders/selectors';
import { TLocation } from '../../../../types';
import { AnimatedDivider } from '../../../../components';

const CHART_HEIGHT = 400;
const LEGEND_HEIGHT = 72;

const StyledChart = styled('div')(({ theme }) => ({
  height: CHART_HEIGHT,
  marginTop: theme.spacing(5),
  '& .apexcharts-canvas svg': {
    height: CHART_HEIGHT,
  },
  '& .apexcharts-canvas svg,.apexcharts-canvas foreignObject': {
    overflow: 'visible',
  },
  '& .apexcharts-legend': {
    height: LEGEND_HEIGHT,
    alignContent: 'center',
    borderTop: `solid 1px ${theme.palette.divider}`,
    top: `calc(${CHART_HEIGHT - LEGEND_HEIGHT}px) !important`,
  },
}));

type TLocationSalesPercentage = {
  locationName: string;
  locationsWithColors: Array<TLocation & { color: string }>;
};

const LocationSalesPercentage: FC<TLocationSalesPercentage> = ({ locationName, locationsWithColors }) => {
  const { t } = useTranslation('commissions');
  const theme = useTheme();
  const dailyStateSelectedLocation = useAppSelector(getDailyStateSelectedLocation);
  const chartRef = useRef<any>(null);
  const locationStats = useAppSelector(getLocationStats);
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);

  const { chartData, selectedLocationPercentage } = useMemo(() => {
    const totalNetSales = locationStats.reduce((sum, location) => sum + (location.netSales || 0), 0);

    const selectedLocationData = dailyStateSelectedLocation !== 0
      ? locationStats.find((location) => location.locationId === dailyStateSelectedLocation)
      : null;

    const selectedLocationPercentage = selectedLocationData
      ? ((selectedLocationData.netSales / totalNetSales) * 100).toFixed(2)
      : '100.00';

    const chartData = {
      categories: locationStats.map((location) => location.name || 'Unknown Location'),
      seriesData: locationStats.map((location) => ((location.netSales / totalNetSales) * 100).toFixed(2)),
    };

    return {
      chartData,
      selectedLocationPercentage,
    };
  }, [locationStats, dailyStateSelectedLocation]);

  // Extract colors from locationsWithColors based on the category names in chartData
  const colors = chartData.categories.map((category) => {
    const matchingLocation = locationsWithColors.find((loc) => loc.name === category);
    return matchingLocation ? matchingLocation.color : '#ccc'; // Default to gray if not found
  });

  useEffect(() => {
    if (chartRef.current) {
      try {
        chartRef.current.updateOptions({
          labels: chartData.categories || ['N/A'],
          plotOptions: {
            pie: {
              donut: {
                size: '90%',
                labels: {
                  value: {
                    formatter: (val: string) => `${parseFloat(val || '0').toFixed(2)}%`,
                  },
                  total: {
                    formatter: () => (
                      dailyStateSelectedLocation === 0
                        ? 'Total: 100%'
                        : `${locationName}: ${selectedLocationPercentage}%`
                    ),
                  },
                },
              },
            },
          },
        }, true, true);
      } catch (error) {
        console.error('Failed to update chart options:', error);
      }
    }
  }, [locationName, selectedLocationPercentage, dailyStateSelectedLocation]);

  const chartOptions: ApexOptions = useChart({
    chart: {
      sparkline: { enabled: true },
      events: {
        mounted: (chart) => {
          chartRef.current = chart;
        },
      },
    },
    colors, // Apply colors dynamically
    labels: chartData.categories || ['N/A'],
    stroke: { colors: [theme.palette.background.paper] },
    legend: { floating: true, horizontalAlign: 'center' },
    tooltip: {
      fillSeriesColor: false,
      y: {
        formatter: (value: number) => `${(value || 0).toFixed(2)}%`,
        title: {
          formatter: (seriesName: string) => seriesName,
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '90%',
          labels: {
            value: {
              formatter: (val: string) => `${parseFloat(val || '0').toFixed(2)}%`,
            },
            total: {
              formatter: () => (
                dailyStateSelectedLocation === 0
                  ? 'Total: 100%'
                  : `${locationName}: ${selectedLocationPercentage}%`
              ),
            },
          },
        },
      },
    },
  });

  const validSeriesData = chartData.seriesData.map(Number) || [0];

  return (
    <Card>
      <CardHeader
        sx={{
          p: 2,
        }}
        title={t('percentageOfTotalSales')}
      />
      <AnimatedDivider loading={isOrdersLoading} />
      <Box sx={{ mt: 3, mx: 3 }} dir="ltr">
        {isOrdersLoading ? (
          <Box pt={3}>
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={40} />
          </Box>
        ) : (
          <StyledChart dir="ltr">
            {chartData.categories.length ? (
              <Chart
                type="donut"
                series={validSeriesData}
                options={chartOptions}
                height={250}
              />
            ) : <CustomNoRowsOverlay text={t('noDataForToday', { ns: 'common' })} />}
          </StyledChart>
        )}
      </Box>
    </Card>
  );
};

export { LocationSalesPercentage };
